/**
 * Read-only side panel showing what a customer type's recipe assembles
 * into — the exact configuration shape the dataplane consumes. Replaces
 * "read the raw payload in Monaco" now that there's no raw payload to read:
 * this is the closest thing to it, but it's always derived, never edited.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { getComposedPayload } from "@/api/components";
import { ErrorBox, Spinner } from "@/components/Spinner";

export interface ComposedPayloadPreviewProps {
  /** Null until the type has been saved at least once — see below. */
  model: string | null;
  version: string | null;
}

export function ComposedPayloadPreview({ model, version }: ComposedPayloadPreviewProps) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  // Composing requires a persisted recipe (GET .../composed reads it back
  // from the catalog) — a type being created has nothing to compose yet.
  const available = Boolean(model && version);

  const query = useQuery({
    queryKey: ["catalog", "composed", model, version],
    queryFn: () => getComposedPayload(token, model!, version!),
    enabled: !!token && available,
  });

  return (
    <aside className="card max-w-md">
      <details open>
        <summary className="cursor-pointer text-sm font-medium text-ink">
          Preview
        </summary>
        <div className="mt-3">
          {!available && (
            <p className="text-xs text-ink-muted">
              Save this type once to preview the configuration it assembles
              for the dataplane.
            </p>
          )}
          {available && query.isLoading && <Spinner />}
          {available && query.isError && <ErrorBox error={query.error} />}
          {available && query.data && (
            <pre className="max-h-[36rem] overflow-auto rounded border border-line bg-sunken p-3 font-mono text-xs text-ink">
              {JSON.stringify(query.data, null, 2)}
            </pre>
          )}
        </div>
      </details>
    </aside>
  );
}
